import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Boot } from './auth/Boot';
import { InviteAccept, accessLinkToken } from './auth/InviteAccept';
import { Login } from './auth/Login';
import { ApiError, errorMessage, onSignedOut } from './lib/api';
import { meQuery } from './lib/hooks';
import { queryClient } from './lib/queryClient';
import { clearRecent } from './lib/recent';
import { resetRoute } from './lib/router';
import { SESSION_ENDED, endSessionLocally, sessionStore } from './lib/session';
import { useStore } from './lib/store';
import { Shell } from './shell/Shell';
import { IconSprite } from './ui/Icon';
import { Toasts } from './ui/Toasts';
import { TooltipHost } from './ui/Tooltip';

// Decides what the window shows: a one-time access link, the sign-in screen, the app, or why it cannot open yet.

export function App() {
  return (
    <>
      <IconSprite />
      <Screen />
      <TooltipHost />
      <Toasts />
    </>
  );
}

// When the server ends a session this tab was using (signed out elsewhere, removed from the team), the app
// goes back to sign-in with a short explanation.
onSignedOut(() => {
  if (queryClient.getQueryData(['me'])) endSessionLocally(SESSION_ENDED);
});

function Screen() {
  const [linkToken, setLinkToken] = useState(accessLinkToken);
  const notice = useStore(sessionStore, (s) => s.notice);
  const me = useQuery({ ...meQuery, retry: false, enabled: !linkToken });
  const { refetch } = me;
  const retry = useCallback(() => void refetch(), [refetch]);

  if (linkToken) {
    return (
      <InviteAccept
        token={linkToken}
        notice={notice}
        onDone={() => {
          queryClient.clear();
          clearRecent();
          resetRoute();
          setLinkToken(null);
        }}
      />
    );
  }

  if (me.data) return <Shell />;
  if (me.isPending) return <Opening />;

  const err = me.error;
  if (err instanceof ApiError && err.status === 401) return <SignIn notice={notice} />;
  if (err instanceof ApiError && err.status === 503 && err.code === 'starting') {
    return <Starting message={err.message} retry={retry} />;
  }
  if (err instanceof ApiError && err.status === 0) return <Offline retry={retry} />;
  return <Boot state={{ kind: 'error', message: errorMessage(err) }} onRetry={retry} />;
}

/** Shows the sign-in screen with any waiting notice, then clears the notice so it shows once. */
function SignIn({ notice }: { notice: string | null }) {
  const [shown] = useState(notice);
  useEffect(() => {
    if (sessionStore.get().notice) sessionStore.set({ notice: null });
  }, []);
  return <Login notice={shown ?? notice} />;
}

/** A quick load shows nothing at all; a slow one shows the opening screen after a moment. */
function Opening() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 400);
    return () => clearTimeout(t);
  }, []);
  return slow ? <Boot state={{ kind: 'opening' }} onRetry={() => undefined} /> : null;
}

/** The server is up but still preparing (or waiting on its database). Checks every few seconds and opens once ready. */
function Starting({ message, retry }: { message: string; retry: () => void }) {
  const [problem, setProblem] = useState<string | null>(message.startsWith('Clientbook is starting') ? null : message);

  useEffect(() => {
    let stopped = false;
    const check = async () => {
      try {
        const res = await fetch('/healthz', { cache: 'no-store' });
        const body = (await res.json()) as { ready?: boolean; problem?: string | null };
        if (stopped) return;
        if (body.ready) retry();
        else setProblem(body.problem ?? null);
      } catch {
        // The server is restarting; the next check tries again.
      }
    };
    const t = setInterval(() => void check(), 3000);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [retry]);

  return <Boot state={{ kind: 'starting', problem }} onRetry={retry} />;
}

/** The server cannot be reached. Tries again every few seconds and opens as soon as it answers. */
function Offline({ retry }: { retry: () => void }) {
  useEffect(() => {
    const t = setInterval(retry, 5000);
    const online = () => retry();
    window.addEventListener('online', online);
    return () => {
      clearInterval(t);
      window.removeEventListener('online', online);
    };
  }, [retry]);
  return <Boot state={{ kind: 'offline' }} onRetry={retry} />;
}
